import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaPaperclip } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import DemandeNoteFrais from './DemandeNoteFrais';
import { formaterMontant } from './formatAvances';
import { ouvrirJustificatif } from './justificatif';
import ParcoursNoteFrais from './ParcoursNoteFrais';

/**
 * Destination « Notes de frais » de l'espace collaborateur (Yros/Kavero#18).
 *
 * Le salarié y dépose une dépense avec son reçu, puis suit ses notes : où en
 * est la décision, et quand le montant arrive sur son bulletin. Une carte par
 * note : une colonne sur le téléphone, et autant que la largeur en permet
 * au-delà (Yros/Kavero#437).
 *
 * **Le filtrage par type n'est pas cosmétique.** Le socle rend toutes les
 * demandes du salarié dans la même enveloppe : sans ce filtre, la page
 * afficherait des congés et des avances sous un titre qui parle de frais.
 */

const COULEUR_ETAT = {
  validee: 'bg-green-100 text-green-800',
  refusee: 'bg-red-100 text-red-800',
};
const COULEUR_ETAT_DEFAUT = 'bg-yellow-100 text-yellow-800';

/**
 * Une note en carte, pensée d'abord pour le téléphone (Yros/Kavero#437) : ce
 * qui a été dépensé et combien en tête, l'état sous le montant, puis le
 * parcours daté de la note, les reçus et, s'il y a lieu, le motif du refus.
 * Rien n'est tronqué : un nom de reçu sans espace se coupe où il peut.
 */
function CarteNote({ demande, locale, onJustificatif }) {
  const { t } = useTranslation();
  const contenu = demande.contenu || {};
  return (
    <li
      data-testid={`notes-frais-ligne-${demande.id}`}
      className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
    >
      {/* Grille à deux colonnes : la nature et sa description prennent la
          place qui reste, le montant et l'état restent entiers à droite. Un
          montant large écrasait sinon le texte, jusqu'à couper « Hébergement ». */}
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1">
        <p className="font-medium text-slate-900">
          {t(`notesFrais.natures.${contenu.nature}`, { defaultValue: contenu.nature || '—' })}
        </p>
        <span className="text-base font-semibold text-slate-900 tabular-nums whitespace-nowrap">
          {formaterMontant(contenu.montant, locale)}
        </span>
        <p className="text-sm text-slate-600 break-words">{contenu.description || ''}</p>
        <span
          className={`justify-self-end inline-flex rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap ${
            COULEUR_ETAT[demande.etat] || COULEUR_ETAT_DEFAUT
          }`}
        >
          {t(`notesFrais.etats.${demande.etat}`, { defaultValue: demande.etat })}
        </span>
      </div>

      <div className="border-t border-slate-100 pt-3">
        <ParcoursNoteFrais
          demande={demande}
          locale={locale}
          testIdVersement={`notes-frais-versement-${demande.id}`}
        />
      </div>

      {(contenu.justificatifs || []).length > 0 && (
        <div className="flex flex-col items-start gap-1.5">
          <span className="text-xs text-slate-500">{t('notesFrais.justificatifs')}</span>
          {contenu.justificatifs.map((piece) => (
            <button
              key={piece.id}
              type="button"
              data-testid={`notes-frais-justificatif-${demande.id}-${piece.id}`}
              onClick={() => onJustificatif(demande, piece)}
              aria-label={`${t('notesFrais.voirJustificatif')} : ${piece.nom}`}
              className="inline-flex min-h-[2.25rem] max-w-full items-center gap-2 rounded-lg bg-slate-50 px-3 py-1.5 text-left text-sm text-[--color-primary] hover:bg-slate-100"
            >
              <FaPaperclip className="flex-shrink-0" aria-hidden="true" />
              <span className="break-words [overflow-wrap:anywhere]">{piece.nom}</span>
            </button>
          ))}
        </div>
      )}

      {demande.motif_refus && (
        <p className="text-sm text-red-800 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          <span className="font-medium">{t('notesFrais.motifRefus')} : </span>
          {demande.motif_refus}
        </p>
      )}
    </li>
  );
}

function MesNotesFrais() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  const [deposees, setDeposees] = useState(0);
  const [notes, setNotes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [erreurAction, setErreurAction] = useState(null);

  // La dernière requête lancée fait foi (même garde que « Avances et prêts »).
  const derniereRequete = useRef(0);

  const charger = useCallback(async () => {
    const numero = ++derniereRequete.current;
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await apiService.listerDemandesRh();
      if (numero !== derniereRequete.current) return;
      setNotes((reponse?.demandes || []).filter((demande) => demande.type === 'note_frais'));
    } catch (e) {
      if (numero !== derniereRequete.current) return;
      signalerErreur(e, 'MesNotesFrais.charger');
      setNotes([]);
      setErreur(t('notesFrais.erreurGenerique'));
    } finally {
      if (numero === derniereRequete.current) setChargement(false);
    }
  }, [t]);

  useEffect(() => {
    charger();
  }, [charger, deposees]);

  const voirJustificatif = async (demande, piece) => {
    setErreurAction(null);
    try {
      await ouvrirJustificatif(demande, piece);
    } catch (e) {
      signalerErreur(e, 'MesNotesFrais.voirJustificatif');
      setErreurAction(t('notesFrais.justificatifIndisponible'));
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg sm:text-xl font-bold text-slate-800">{t('notesFrais.titre')}</h1>
        {!formulaireOuvert && (
          <button
            type="button"
            data-testid="notes-frais-nouvelle"
            onClick={() => setFormulaireOuvert(true)}
            className="flex w-full sm:w-auto items-center justify-center gap-2 bg-[--color-primary] hover:bg-[--bg-hover] text-white px-4 py-2.5 rounded-lg shadow transition"
          >
            <FaPlus aria-hidden="true" />
            <span>{t('notesFrais.nouvelle')}</span>
          </button>
        )}
      </div>

      {formulaireOuvert && (
        <div className="space-y-2">
          <button
            type="button"
            data-testid="notes-frais-annuler"
            onClick={() => setFormulaireOuvert(false)}
            className="text-sm text-slate-600 hover:text-slate-900 underline"
          >
            {t('notesFrais.annuler')}
          </button>
          <DemandeNoteFrais onDepot={() => setDeposees((n) => n + 1)} />
        </div>
      )}

      {(erreur || erreurAction) && (
        <p
          data-testid="notes-frais-erreur"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
        >
          {erreur || erreurAction}
        </p>
      )}

      {chargement ? (
        <LoadingBarWithStyles />
      ) : !erreur && (
        <section aria-labelledby="titre-notes-frais" className="space-y-3">
          <h2 id="titre-notes-frais" className="text-sm sm:text-base font-semibold text-slate-900">
            {t('notesFrais.listeTitre')}
            <span className="ml-2 text-sm font-normal text-slate-500 tabular-nums">{notes.length}</span>
          </h2>
          {notes.length === 0 ? (
            <p
              data-testid="notes-frais-vide"
              className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-10 text-center text-sm text-slate-500"
            >
              {t('notesFrais.vide')}
            </p>
          ) : (
            // Autant de colonnes que la place en permet, 18 rem au moins par
            // carte : la largeur utile dépend du menu latéral, pas de l'écran.
            <ul aria-label={t('notesFrais.listeTitre')} className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(100%,18rem),1fr))] items-start">
              {notes.map((demande) => (
                <CarteNote key={demande.id} demande={demande} locale={locale} onJustificatif={voirJustificatif} />
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

export default MesNotesFrais;
